import { FC, useMemo } from "react";
import { format } from "date-fns";
import { Badge, Box, Tooltip, useTheme } from "@mui/material";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers-pro";
import __ from "src/utils/Translations";
import findInAbsenceRequestDays from "src/utils/Company/Absences/findInAbsenceRequestDays";
import isDayExcluded from "src/utils/Company/Absences/isDayExcluded";
import isOutsideSubmitLimitation from "src/utils/Company/Absences/isOutsideSubmitLimitation";
import { IAbsenceRequestDay } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";
import {
  getAbsenceRequestDayStyles,
  getPickerStyles,
  getRangeIntervalStyles,
} from "./Datepicker.styles";
import SubmitLimitationTooltip, {
  SUBMIT_LIMITATION_TOOLTIP_KEY,
} from "./SubmitLimitationTooltip";

interface ICustomPickersDayProps extends PickersDayProps {
  absenceRequestDays: IAbsenceRequestDay[];
  excludedDays: IExcludedDay[];
  plannedDays: string[];
  multipleRequestsPerDayAllowed: boolean;
  billInCalendarDays: boolean;
  submitLimitationFrom: string; // ISO date in format YYYY-MM-DD
  submitLimitationTo: string; // ISO date in format YYYY-MM-DD
}

const CustomPickersDay: FC<ICustomPickersDayProps> = ({
  absenceRequestDays = [],
  excludedDays = [],
  plannedDays = [],
  multipleRequestsPerDayAllowed,
  billInCalendarDays,
  submitLimitationFrom,
  submitLimitationTo,
  ...props
}) => {
  const theme = useTheme();

  const { day, outsideCurrentMonth } = props as PickersDayProps;
  const parsedToISO = format(new Date(day), "yyyy-MM-dd");
  const requestDay = findInAbsenceRequestDays(day, absenceRequestDays);
  const isRequestDay = !!requestDay && requestDay.absenceName;
  // Normal excluded days (i.e. weekends or holidays)
  const isExcluded = isDayExcluded(day, excludedDays);
  // Excluded days on absence types counting excluded days to balance
  const isCustomExcluded = billInCalendarDays && isExcluded;
  const plannedDaysSet = useMemo(() => new Set(plannedDays), [plannedDays]);
  const isPlannedDay = plannedDaysSet.has(parsedToISO);

  const isLimitationDisabled = isOutsideSubmitLimitation(
    day,
    submitLimitationFrom,
    submitLimitationTo
  );

  if (isRequestDay) {
    const isDisabled =
      isLimitationDisabled ||
      !multipleRequestsPerDayAllowed ||
      !requestDay.multipleRequestsPerDayAllowed;

    const dayElement = (
      <PickersDay
        {...props}
        disabled={isDisabled}
        sx={getPickerStyles(
          !!isRequestDay,
          requestDay?.bgColor,
          requestDay?.position
        )}
        data-testid="absence-datepicker-request-day"
      >
        <Tooltip
          title={
            isLimitationDisabled
              ? __(SUBMIT_LIMITATION_TOOLTIP_KEY)
              : requestDay.absenceName
          }
          placement="top"
          arrow
        >
          <Box
            component="span"
            sx={getAbsenceRequestDayStyles(requestDay?.color)}
          >
            {format(day, "d")}
          </Box>
        </Tooltip>
      </PickersDay>
    );

    return isPlannedDay && !outsideCurrentMonth ? (
      <Badge badgeContent="" variant="dot" color="primary" overlap="circular">
        {dayElement}
      </Badge>
    ) : (
      dayElement
    );
  }

  const dayElement = (
    <PickersDay
      {...props}
      disabled={isLimitationDisabled}
      sx={getRangeIntervalStyles(theme, isExcluded, isCustomExcluded)}
      data-testid="absence-datepicker-day"
    />
  );

  const wrappedDayElement = isLimitationDisabled ? (
    <SubmitLimitationTooltip>{dayElement}</SubmitLimitationTooltip>
  ) : (
    dayElement
  );

  return isPlannedDay && !outsideCurrentMonth ? (
    <Badge badgeContent="" variant="dot" color="primary" overlap="circular">
      {wrappedDayElement}
    </Badge>
  ) : (
    wrappedDayElement
  );
};

export default CustomPickersDay;
